import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import {
  eventQueries,
  type EventFilterInput,
} from "../features/events/api/queries";
import {
  DEFAULT_PRESET,
  excludesPast,
  isValidDate,
  PRESET_LABELS,
  reachesPast,
  resolveRange,
  toInstants,
  type DateRange,
  type RangePreset,
} from "../features/events/date-range";
import { DateRangeFilter } from "../features/events/components/DateRangeFilter";
import { EventList } from "../features/events/components/EventList";
import { tagQueries } from "../features/tags/api/queries";
import { TagFilterBar } from "../features/tags/components/TagFilterBar";
import type { TagMatch } from "../features/tags/types";
import { toOffset, toPage } from "../shared/lib/paging";
import { AppHeader } from "../shared/ui/AppHeader";
import { Pager } from "../shared/ui/Pager";

/**
 * How many events a page of the listing holds.
 *
 * Each row resolves organisers, links, images, a venue, an area and a forecast,
 * so the cost of a page is the page size times that breadth — which is why the
 * number is small rather than "as many as fit on a screen".
 */
const PAGE_SIZE = 20;

/**
 * URL shape: `?tags=salsa,live-music&match=ALL&range=MONTH&past=true`, plus
 * `&from=2026-12-24&to=2026-12-26` under `range=CUSTOM`.
 *
 * All optional, and validation returns them as `undefined` rather than as their
 * defaults when unset — the router serialises whatever validation returns, so a
 * present-but-default value would leave an unfiltered listing sitting at
 * `?tags=%5B%5D&match=ANY&range=WEEK`. Absent means absent.
 *
 * `tags` stays a comma string in the URL (rather than the router's default array
 * encoding) so the link reads as `?tags=salsa,live-music`.
 */
interface EventsSearch {
  tags?: string;
  match?: "ALL";
  /** Absent means the default, {@link DEFAULT_PRESET}. */
  range?: RangePreset;
  /** Present only under `range=CUSTOM`. */
  from?: string;
  to?: string;
  /**
   * Show past events. Absent rather than `false` when off, since off is the
   * default.
   *
   * A boolean rather than a `"1"` string: the router JSON-encodes search values,
   * so a string arrives as `past=%221%22` — quotes and all — where `true`
   * serialises as the plain `past=true` a hand-typed URL would use. The other
   * params here dodge that only because they are re-parsed on the way in
   * regardless.
   *
   * Absent under a past-reaching range, because it is implied there: the range
   * already says the past is wanted, and carrying a redundant `past=true` would
   * let a hand-edited URL contradict itself.
   */
  past?: true;
  /**
   * Which page, 1-based. Absent means the first.
   *
   * In the URL like every other bit of listing state, so a page is linkable and
   * survives Back — the same reasoning as the filters. **1-based rather than a
   * raw offset**: the number in the URL is the one shown in the pager, and a
   * `?offset=40` that displays as "page 3" is a needless translation for anyone
   * reading or editing the address bar.
   */
  page?: number;
}

/** The validated filter, as the component consumes it. */
interface EventsFilterState {
  tags: string[];
  match: TagMatch;
  preset: RangePreset;
  /** Only meaningful under `CUSTOM`; empty otherwise. */
  custom: DateRange;
  /** What the user asked for, before the range's implication is applied. */
  showPast: boolean;
}

/**
 * Filter state lives in the URL so a filtered listing is linkable, survives
 * reload, and works with the back button. Slugs rather than ids keep the link
 * readable and reseed-proof.
 *
 * `validateSearch` is the trust boundary for URL input: anything hand-typed is
 * coerced to a known-good shape here, so downstream code never sees a stray
 * `match=DROP`.
 */
function validateSearch(search: Record<string, unknown>): EventsSearch {
  const slugs = parseSlugs(search.tags);
  const preset = parsePreset(search.range);

  /*
   * Custom bounds are only carried under CUSTOM, and dropped otherwise — a
   * `?range=WEEK&from=2026-01-01` would show one range while the URL claimed
   * another. A reversed pair drops the upper bound rather than erroring: it can
   * only ever match nothing, and an empty list is a worse explanation than a
   * one-sided range the user can see and correct.
   */
  const from = preset === "CUSTOM" ? asDate(search.from) : undefined;
  const rawTo = preset === "CUSTOM" ? asDate(search.to) : undefined;
  const to = from && rawTo && rawTo < from ? undefined : rawTo;

  /*
   * `past` is dropped when the range already implies it, so the two cannot
   * disagree. The component derives the effective value from both.
   *
   * Uses the same snapped `today()` as `toFilter`, not a bare `new Date()`. The
   * answer happens to be the same either way — `reachesPast` compares calendar
   * dates — but "happens to be" is not a property to build the URL contract on.
   */
  const impliedByRange = reachesPast(
    resolveRange(preset, { from, to }, today()),
    today(),
  );

  return {
    tags: slugs.length > 0 ? slugs.join(",") : undefined,
    // ALL only says something with two or more tags; below that it is the
    // same query as ANY, so don't carry it in the URL.
    match: search.match === "ALL" && slugs.length > 1 ? "ALL" : undefined,
    range: preset === DEFAULT_PRESET ? undefined : preset,
    from,
    to,
    past: search.past === true && !impliedByRange ? true : undefined,
    page: parsePage(search.page),
  };
}

/**
 * A page number from the URL, or undefined for the first.
 *
 * Anything unusable — `?page=lol`, `?page=0`, `?page=-3`, `?page=1.5` — becomes
 * the first page rather than an error, matching how `?range=` treats nonsense: a
 * stale or hand-mangled link should open the listing, not a dead end. Page 1 is
 * dropped so the default is not carried in the URL.
 *
 * No upper bound here. How many pages exist depends on the filter and on what is
 * in the database, neither of which this function can see — a page past the end
 * is corrected once the total is known.
 */
function parsePage(raw: unknown): number | undefined {
  const page = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isInteger(page) || page < 2) return undefined;
  return page;
}

/**
 * An unknown `?range=` falls back to the default rather than erroring.
 *
 * Same reasoning as the week view's date handling: a stale bookmark should open
 * the ordinary listing, not a dead end. There is nothing dangerous to guard
 * against here, only nonsense to ignore.
 */
function parsePreset(raw: unknown): RangePreset {
  const known = PRESET_LABELS.some((option) => option.preset === raw);
  return known ? (raw as RangePreset) : DEFAULT_PRESET;
}

function asDate(raw: unknown): string | undefined {
  return typeof raw === "string" && isValidDate(raw) ? raw : undefined;
}

/** Tolerates a comma string or an array, and normalises to unique lowercase slugs. */
function parseSlugs(raw: unknown): string[] {
  const parts = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(",")
      : [];

  const slugs = parts
    .filter((slug): slug is string => typeof slug === "string")
    .map((slug) => slug.trim().toLowerCase())
    .filter((slug) => slug.length > 0);

  // Dedupe so a repeated slug can't skew ALL matching or double a chip.
  return [...new Set(slugs)];
}

/** URL search → the filter the component works with. */
function toFilterState(search: EventsSearch): EventsFilterState {
  const preset = parsePreset(search.range);

  return {
    tags: parseSlugs(search.tags),
    match: search.match === "ALL" ? "ALL" : "ANY",
    preset,
    custom: preset === "CUSTOM" ? { from: search.from, to: search.to } : {},
    showPast: search.past === true,
  };
}

/**
 * **Where the automatic toggle happens.**
 *
 * A range reaching into the past is only ever chosen to see what already
 * happened, so it turns past events on regardless of the checkbox — leaving them
 * hidden would answer "past week" with an empty list, which reads as a broken
 * filter rather than a strict one.
 *
 * Derived here rather than written into the URL on selection, so it cannot fall
 * out of step with the range: a `past=true` persisted at click time would survive a
 * later switch back to a forward range and quietly widen it.
 *
 * `now` threads through so one render resolves the range and this against the same
 * instant.
 */
function resolveShowPast(
  state: EventsFilterState,
  now = today(),
): { showPast: boolean; forced: boolean; inert: boolean } {
  const range = resolveRange(state.preset, state.custom, now);
  const forced = reachesPast(range, now);

  /*
   * The mirror of `forced`. A forward range sends `startsFrom: today`, and the
   * server applies that alongside `upcomingOnly` — so the checkbox cannot widen
   * anything and the listing disables it rather than leaving it dead.
   */
  const inert = excludesPast(range, now);

  return { showPast: forced || state.showPast, forced, inert };
}

/**
 * Local midnight today, as the instant every date calculation on this page starts
 * from.
 *
 * Stable across a render pass, which is what {@link toFilter} needs — see the
 * warning there. Local rather than UTC midnight because the presets are the
 * viewer's own calendar days.
 */
function today(): Date {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

/**
 * Filter state → the GraphQL filter argument.
 *
 * Always an object now, unlike before: the date range and the past-events rule
 * apply to an otherwise-unfiltered listing too, so there is no longer an
 * "unfiltered" case that can send nothing.
 *
 * ⚠️ **The instant is snapped to local midnight**, and that is load-bearing rather
 * than tidiness. `loaderDeps` and the component both build this filter, and the
 * query key is derived from it — so a bare `new Date()` in each would differ by
 * however long the render took, producing two cache entries, a miss on every
 * navigation, and a refetch loop. Every bound this filter produces is a whole-day
 * boundary anyway, so nothing below a day's resolution is being discarded.
 */
function toFilter(state: EventsFilterState): EventFilterInput {
  const now = today();
  const range = resolveRange(state.preset, state.custom, now);
  const { showPast } = resolveShowPast(state, now);

  return {
    ...(state.tags.length > 0
      ? { tagSlugs: state.tags, tagMatch: state.match }
      : {}),
    ...toInstants(range),
    upcomingOnly: !showPast,
  };
}

export const Route = createFileRoute("/_authenticated/events/")({
  validateSearch,
  // Re-run the loader when the filter changes, but not when unrelated search
  // params do.
  loaderDeps: ({ search }) => ({
    filter: toFilter(toFilterState(search)),
    offset: toOffset(search.page, PAGE_SIZE),
  }),
  // The loader warms the cache; the component reads it. Data is ready before
  // the route renders, so there's no loading flash and no fetch waterfall.
  loader: ({ context: { queryClient }, deps }) =>
    Promise.all([
      queryClient.ensureQueryData(
        eventQueries.page({
          filter: deps.filter,
          limit: PAGE_SIZE,
          offset: deps.offset,
        }),
      ),
      queryClient.ensureQueryData(tagQueries.list()),
    ]),
  component: EventsPage,
  errorComponent: ({ error }) => (
    <main className="p-6">
      <p role="alert" className="text-brand">
        Couldn’t load events: {error.message}
      </p>
    </main>
  ),
});

function EventsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const filterState = toFilterState(search);

  // useSuspenseQuery, not useQuery: the loader ensured the data, so `data` is
  // non-nullable and there is no dead isLoading branch.
  const offset = toOffset(search.page, PAGE_SIZE);
  const { data } = useSuspenseQuery(
    eventQueries.page({
      filter: toFilter(filterState),
      limit: PAGE_SIZE,
      offset,
    }),
  );
  const { data: tagData } = useSuspenseQuery(tagQueries.list());

  const events = data.eventPage.items;
  const total = data.eventPage.totalCount;

  /**
   * Writes filter state to the URL; the loader reacts and refetches.
   *
   * `replace` so toggling five tags leaves one history entry rather than five —
   * Back should return to wherever the user came from, not step back through
   * each click. `validateSearch` runs over this, which is what drops the
   * defaults back out.
   *
   * ⚠️ **Merges against the search updater's argument, not against the
   * `filterState` closed over by this render.** Two changes dispatched before a
   * re-render both read the same stale render props otherwise, and the second
   * silently discards the first — which is not hypothetical: filling the custom
   * From and To fields in quick succession lost the From date every time, because
   * the To edit rebuilt the URL from a `custom` that predated it. The updater form
   * is given the current params, so each change composes.
   */
  const setFilter = (next: Partial<EventsFilterState>) => {
    void navigate({
      search: (current: EventsSearch) => {
        const merged = { ...toFilterState(current), ...next };
        return {
          tags: merged.tags.length > 0 ? merged.tags.join(",") : undefined,
          match: merged.match === "ALL" ? "ALL" : undefined,
          range: merged.preset === DEFAULT_PRESET ? undefined : merged.preset,
          /*
           * Custom bounds are carried only under CUSTOM, so switching to a preset
           * clears them from the URL rather than leaving them to reappear if the
           * user switches back — the pickers should not silently remember a range
           * that is no longer shown anywhere.
           */
          from: merged.preset === "CUSTOM" ? merged.custom.from : undefined,
          to: merged.preset === "CUSTOM" ? merged.custom.to : undefined,
          past: merged.showPast ? true : undefined,
        };
      },
      replace: true,
    });
  };

  /*
   * Takes the updater form for the same reason as `setFilter` above: toggling two
   * tags faster than a render would otherwise compute both from the same starting
   * set, and the second would drop the first.
   */
  const toggleTag = (slug: string) => {
    void navigate({
      search: (current: EventsSearch) => {
        const tags = parseSlugs(current.tags);
        const next = tags.includes(slug)
          ? tags.filter((candidate) => candidate !== slug)
          : [...tags, slug];

        return {
          ...current,
          tags: next.length > 0 ? next.join(",") : undefined,
          // ALL says nothing below two tags, and validateSearch drops it there —
          // mirrored here so the URL this builds is already in its final shape.
          match: current.match === "ALL" && next.length > 1 ? "ALL" : undefined,
        };
      },
      replace: true,
    });
  };

  const { showPast, forced, inert } = resolveShowPast(filterState);

  /*
   * Whether the empty state should offer to create an event or suggest loosening
   * the filter. The date range counts, and the default one counts too: "no events
   * in the coming week" is over-filtering from the reader's point of view, and
   * inviting them to create an event would be answering a question they did not
   * ask.
   */
  const isFiltered =
    filterState.tags.length > 0 || filterState.preset !== "ALL";

  return (
    <>
      <AppHeader />
      <main className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Events</h1>
            <p className="mt-1 text-sm text-content-muted">
              {/* The total, not this page's length — "23 matching events"
                  should not read "20" because that is all one page holds. */}
              {isFiltered
                ? `${String(total)} matching ${
                    total === 1 ? "event" : "events"
                  }, ordered by start time.`
                : "Everything on, ordered by start time."}
            </p>
          </div>
          <Link
            to="/events/new"
            className="rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
          >
            New event
          </Link>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <DateRangeFilter
            preset={filterState.preset}
            custom={filterState.custom}
            showPast={showPast}
            forcedShowPast={forced}
            rangeExcludesPast={inert}
            onPresetChange={(preset) => {
              setFilter({ preset });
            }}
            onCustomChange={(custom) => {
              setFilter({ custom });
            }}
            onShowPastChange={(next) => {
              setFilter({ showPast: next });
            }}
          />
          <TagFilterBar
            tags={tagData.tags}
            selected={filterState.tags}
            match={filterState.match}
            onToggle={toggleTag}
            onMatchChange={(match) => {
              setFilter({ match });
            }}
            onClear={() => {
              setFilter({ tags: [] });
            }}
          />
        </div>

        <div className="mt-6">
          <EventList
            events={events}
            isFiltered={isFiltered}
            hasTagFilter={filterState.tags.length > 0}
          />

          <Pager
            offset={offset}
            limit={PAGE_SIZE}
            totalCount={total}
            /*
             * Writes the page to the URL rather than to component state, so it
             * is linkable and survives Back like every other listing choice.
             * `replace: false` here, unlike the filter updates: paging is
             * navigation, and Back should step to the previous page.
             */
            onOffsetChange={(next) => {
              void navigate({
                search: (current) => ({
                  ...current,
                  page: next === 0 ? undefined : toPage(next, PAGE_SIZE),
                }),
              });
            }}
            noun="events"
          />
        </div>
      </main>
    </>
  );
}
